import { z } from 'zod';

/**
 * F60 — Ops logs & API health (plans/F60_admin_logs.md).
 *
 * Super-admin-only reads of the production API log group in CloudWatch:
 * log search (Logs Insights), live tail (FilterLogEvents), level / status
 * volume, latency percentiles and a paged CSV export. Every route sits under
 * `/admin/ops/logs` behind permission `ops_logs:read`.
 *
 * Lines are redacted for SECRETS only (tokens, OTPs, passwords, ID numbers);
 * emails and IPs are returned as logged (founder decision, 2026-10-29).
 */

// ─── Levels ──────────────────────────────────────────────────────────────────

export const OPS_LOG_LEVELS = ['trace', 'debug', 'info', 'warn', 'error', 'fatal'] as const;
export type OpsLogLevel = (typeof OPS_LOG_LEVELS)[number];

/** pino's numeric levels. */
export const OPS_LOG_LEVEL_NUMBERS: Readonly<Record<OpsLogLevel, number>> = {
  trace: 10,
  debug: 20,
  info: 30,
  warn: 40,
  error: 50,
  fatal: 60,
};

export const OPS_HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS'] as const;
export type OpsHttpMethod = (typeof OPS_HTTP_METHODS)[number];

// ─── Caps ────────────────────────────────────────────────────────────────────

/** Widest query window = the log group's 7-day retention (Insights bills per GB scanned). */
export const OPS_LOGS_MAX_RANGE_HOURS = 168;
export const OPS_LOGS_EVENTS_MAX_LIMIT = 200;
export const OPS_LOGS_EVENTS_DEFAULT_LIMIT = 100;
/** Live tail never looks further back than this. */
export const OPS_LOGS_RECENT_WINDOW_MINUTES = 5;
export const OPS_LOGS_EXPORT_PAGE_ROWS = 1000;
export const OPS_LOGS_EXPORT_MAX_ROWS = 10_000;

// ─── Query schemas ───────────────────────────────────────────────────────────

const isoDateTime = z.string().datetime({ offset: true });

/** No control characters — the text is embedded in a Logs Insights string literal. */
const PRINTABLE = /^\P{Cc}*$/u;

const filterShape = {
  from: isoDateTime,
  to: isoDateTime,
  minLevel: z.enum(OPS_LOG_LEVELS).optional(),
  method: z.enum(OPS_HTTP_METHODS).optional(),
  /** A registered route pattern, e.g. `/admin/venues/:id` (see `OpsLogsInfo.routes`). */
  route: z.string().min(1).max(300).startsWith('/').regex(PRINTABLE).optional(),
  /** Exact status (`404`) or a class (`5xx`). */
  status: z
    .string()
    .regex(/^[1-5](?:\d\d|xx)$/u, 'expected e.g. 404 or 5xx')
    .optional(),
  requestId: z
    .string()
    .regex(/^[A-Za-z0-9-]{1,64}$/u)
    .optional(),
  userId: z.string().uuid().optional(),
  adminId: z.string().uuid().optional(),
  /** Case-sensitive substring of the raw line. */
  text: z.string().min(1).max(200).regex(PRINTABLE).optional(),
};

export const OpsLogsRangeQuerySchema = z.object({ from: isoDateTime, to: isoDateTime }).strict();
export type OpsLogsRangeQuery = z.infer<typeof OpsLogsRangeQuerySchema>;

export const OpsLogsEventsQuerySchema = z
  .object({
    ...filterShape,
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(OPS_LOGS_EVENTS_MAX_LIMIT)
      .optional()
      .default(OPS_LOGS_EVENTS_DEFAULT_LIMIT),
    cursor: z.string().min(1).max(4000).optional(),
  })
  .strict();
export type OpsLogsEventsQuery = z.infer<typeof OpsLogsEventsQuerySchema>;

export const OpsLogsExportQuerySchema = z
  .object({ ...filterShape, cursor: z.string().min(1).max(4000).optional() })
  .strict();
export type OpsLogsExportQuery = z.infer<typeof OpsLogsExportQuerySchema>;

export const OpsLogsRecentQuerySchema = z
  .object({
    /** Only lines strictly newer than this (live-tail polling). */
    since: isoDateTime.optional(),
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(OPS_LOGS_EVENTS_MAX_LIMIT)
      .optional()
      .default(OPS_LOGS_EVENTS_DEFAULT_LIMIT),
    minLevel: z.enum(OPS_LOG_LEVELS).optional(),
  })
  .strict();
export type OpsLogsRecentQuery = z.infer<typeof OpsLogsRecentQuerySchema>;

/** The filter subset that narrows a search (everything but range / paging). */
export type OpsLogsFilters = Omit<OpsLogsExportQuery, 'from' | 'to' | 'cursor'>;

// ─── Read shapes ─────────────────────────────────────────────────────────────

export interface OpsLogEvent {
  /** Stable id: sha1(timestamp | raw line), 16 hex chars — dedupes tail + search rows. */
  readonly id: string;
  /** ISO-8601 UTC. */
  readonly timestamp: string;
  /** `null` for lines that are not pino JSON (npm banners, crash output). */
  readonly level: OpsLogLevel | null;
  readonly msg: string | null;
  readonly reqId: string | null;
  readonly method: string | null;
  /** Route pattern (F60 request lines). */
  readonly route: string | null;
  /** Request URL from pre-F60 `incoming request` lines (secrets redacted). */
  readonly url: string | null;
  readonly statusCode: number | null;
  /** Milliseconds. */
  readonly responseTime: number | null;
  readonly userId: string | null;
  readonly adminId: string | null;
  readonly event: string | null;
  readonly action: string | null;
  /** `err.message` (or a string `err`). */
  readonly error: string | null;
  /** The whole redacted JSON line; `null` for non-JSON lines. */
  readonly data: Readonly<Record<string, unknown>> | null;
  /** The redacted raw text of a non-JSON line; `null` for JSON lines. */
  readonly raw: string | null;
}

export interface OpsLogsScanMeta {
  readonly bytesScanned: number;
  readonly recordsScanned: number;
  /** Served from the API's short-lived cache (no new scan). */
  readonly cached: boolean;
}

export interface OpsLogsEventsPage {
  readonly data: readonly OpsLogEvent[];
  /** Signed continuation token for "load older"; `null` when exhausted. */
  readonly nextCursor: string | null;
  readonly meta: OpsLogsScanMeta;
}

export type OpsLogsProviderMode = 'cloudwatch' | 'fake' | 'unconfigured';

export interface OpsLogsInfo {
  readonly mode: OpsLogsProviderMode;
  readonly configured: boolean;
  readonly logGroup: string | null;
  readonly region: string | null;
  readonly retentionDays: number | null;
  readonly storedBytes: number | null;
  readonly maxRangeHours: number;
  /** Registered routes as `METHOD /pattern`, sorted (feeds the route filter). */
  readonly routes: readonly string[];
}

export type OpsLevelBucket = { readonly t: string } & Readonly<Record<OpsLogLevel, number>>;

export interface OpsStatusBucket {
  readonly t: string;
  readonly s2xx: number;
  readonly s3xx: number;
  readonly s4xx: number;
  readonly s5xx: number;
}

export interface OpsLogsStats {
  readonly binSeconds: number;
  readonly levels: readonly OpsLevelBucket[];
  readonly requests: readonly OpsStatusBucket[];
}

export interface OpsLatencyPercentiles {
  readonly requests: number;
  readonly p50: number | null;
  readonly p90: number | null;
  readonly p99: number | null;
}

export interface OpsLatencyOverall extends OpsLatencyPercentiles {
  readonly max: number | null;
  readonly s4xx: number;
  readonly s5xx: number;
}

export interface OpsLatencyPoint extends OpsLatencyPercentiles {
  readonly t: string;
  readonly s5xx: number;
}

export interface OpsRouteLatency extends OpsLatencyPercentiles {
  readonly method: string;
  readonly route: string;
  readonly max: number | null;
  readonly s4xx: number;
  readonly s5xx: number;
}

export interface OpsLatencyReport {
  readonly binSeconds: number;
  readonly overall: OpsLatencyOverall;
  readonly series: readonly OpsLatencyPoint[];
  readonly routes: readonly OpsRouteLatency[];
  /** More routes than the per-route limit were seen. */
  readonly routesTruncated: boolean;
}

export interface OpsLogsExportPage {
  /** CSV text for this page; the first page starts with the header row. */
  readonly csv: string;
  readonly rows: number;
  readonly nextCursor: string | null;
  /** The export stopped at OPS_LOGS_EXPORT_MAX_ROWS. */
  readonly truncated: boolean;
}

// ─── Errors ──────────────────────────────────────────────────────────────────

export const OPS_LOGS_ERROR_CODES = [
  'OPS_LOGS_NOT_CONFIGURED',
  'OPS_LOGS_ACCESS_DENIED',
  'OPS_LOGS_RANGE_INVALID',
  'OPS_LOGS_RANGE_TOO_LARGE',
  'OPS_LOGS_ROUTE_UNKNOWN',
  'OPS_LOGS_CURSOR_INVALID',
  'OPS_LOGS_THROTTLED',
  'OPS_LOGS_QUERY_TIMEOUT',
  'OPS_LOGS_UPSTREAM',
] as const;
export type OpsLogsErrorCode = (typeof OPS_LOGS_ERROR_CODES)[number];
